import React, { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { useOrganisation } from './OrganisationContext';

/**
 * Sélecteur « Établissement · Service » en haut de la barre latérale (maquette L01-01).
 * Absent tant que le compte n'a qu'un seul service. Une pastille dit le rôle de la personne.
 */
export const SelecteurContexte: React.FC<{ collapsed?: boolean }> = ({ collapsed = false }) => {
    const { contexte, serviceActif, choisirService } = useOrganisation();
    const [ouvert, setOuvert] = useState(false);
    const racine = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ouvert) return undefined;
        const fermer = (e: MouseEvent) => { if (racine.current && !racine.current.contains(e.target as Node)) setOuvert(false); };
        const echap = (e: KeyboardEvent) => { if (e.key === 'Escape') setOuvert(false); };
        document.addEventListener('mousedown', fermer);
        document.addEventListener('keydown', echap);
        return () => { document.removeEventListener('mousedown', fermer); document.removeEventListener('keydown', echap); };
    }, [ouvert]);

    if (!contexte || !contexte.selecteur_visible || !serviceActif) return null;
    const role = contexte.role === 'direction' ? 'Direction' : 'Collaborateur';

    return (
        <div ref={racine} className="relative mb-2 shrink-0" data-testid="selecteur-contexte">
            <button
                type="button"
                onClick={() => setOuvert((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={ouvert}
                title={collapsed ? `${contexte.etablissement.name} · ${serviceActif.name}` : undefined}
                className={`w-full flex items-center ${collapsed ? 'justify-center px-0' : 'gap-2 px-2.5'} min-h-[44px] md:min-h-[40px] rounded-xl border border-border bg-surface hover:border-content transition-colors text-left`}
            >
                <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--acc-bg)', color: 'var(--acc-fg)' }}>
                    <Building2 size={15} />
                </span>
                {!collapsed && (
                    <>
                        <span className="min-w-0 flex-1">
                            <span className="block text-[12.5px] font-semibold text-content truncate">{contexte.etablissement.name}</span>
                            <span className="block text-[11.5px] text-content-muted truncate">{serviceActif.name}</span>
                        </span>
                        <ChevronsUpDown size={14} className="text-content-muted shrink-0" />
                    </>
                )}
            </button>
            {ouvert && (
                <div role="listbox" aria-label="Établissement et service" className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-surface border border-border rounded-xl p-1.5 shadow-md min-w-[220px]">
                    <div className="px-2.5 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-[.09em] text-content-muted truncate">{contexte.etablissement.name}</div>
                    {contexte.services.map((s) => {
                        const actif = s.id === serviceActif.id;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                role="option"
                                aria-selected={actif}
                                onClick={() => { choisirService(s.id); setOuvert(false); }}
                                className={`w-full flex items-center gap-2 min-h-[40px] px-2.5 rounded-lg text-left text-[13px] ${actif ? 'bg-surface-soft font-semibold text-content' : 'text-content-muted hover:bg-surface-soft hover:text-content'}`}
                            >
                                <span className="flex-1 min-w-0 truncate">{s.name}</span>
                                <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-surface-soft text-content-muted border border-border">{role}</span>
                                {actif && <Check size={14} aria-hidden />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
